import { useEffect } from 'react';

/* ─────────────────────────────────────────────
   KFX JOURNAL — TERMS OF SERVICE
   Effective date: June 2026
───────────────────────────────────────────── */

const LAST_UPDATED = 'June 6, 2026';
const CONTACT_EMAIL = 'info@kfxjournal.com';
const COMPANY_NAME = 'KFX Journal';

const sections = [
  {
    id: 'acceptance',
    title: '1. Acceptance of Terms',
    content: [
      {
        type: 'text',
        text: `These Terms of Service ("Terms") govern your access to and use of ${COMPANY_NAME} ("KFX", "the platform", "we", "us"). By creating an account, importing trade data, or using any feature of KFX Journal, you agree to be bound by these Terms.`,
      },
      {
        type: 'text',
        text: 'If you do not agree to these Terms, do not use KFX Journal. If you are using the platform on behalf of an organisation, you represent that you have the authority to bind that organisation to these Terms.',
      },
      {
        type: 'text',
        text: 'You must be at least 18 years old to use KFX Journal. By using the platform, you confirm you meet this requirement.',
      },
    ],
  },
  {
    id: 'description',
    title: '2. What KFX Journal Does',
    content: [
      {
        type: 'text',
        text: 'KFX Journal is a trading intelligence platform designed to help forex traders analyse their performance and improve their consistency. The platform provides:',
      },
      {
        type: 'list',
        items: [
          'A trade journal for logging and reviewing individual trade records',
          'Analytics dashboards including win rate, average R:R, equity curve, session heatmaps, and rule adherence scoring',
          'MT4/MT5 CSV trade history import',
          'Execution grading tools (A/B/C scoring)',
          'An AI coaching feature powered by Anthropic\'s Claude that generates personalised performance reports based on your trade history',
          'A What-If trade simulator to model performance under different discipline scenarios',
          'Prop firm tracking tools for FTMO, FundedNext, and similar challenges',
          'Position sizing and drawdown recovery calculators',
        ],
      },
      {
        type: 'highlight',
        text: 'Important: KFX Journal is a performance tracking and coaching tool. It is not a financial adviser, broker, or regulated investment service. Nothing on the platform constitutes financial advice. All trading decisions are solely your own responsibility.',
      },
    ],
  },
  {
    id: 'account',
    title: '3. Your Account',
    content: [
      {
        type: 'subheading',
        text: '3.1 Registration',
      },
      {
        type: 'text',
        text: 'To use KFX Journal you must create an account with a valid email address and a password. You are responsible for maintaining the confidentiality of your credentials. Do not share your password with anyone.',
      },
      {
        type: 'subheading',
        text: '3.2 Account Security',
      },
      {
        type: 'text',
        text: 'You are responsible for all activity that occurs under your account. Notify us immediately at info@kfxjournal.com if you suspect unauthorised access to your account. We will not be liable for any loss or damage arising from unauthorised account access where you failed to keep your credentials secure.',
      },
      {
        type: 'subheading',
        text: '3.3 Accurate Information',
      },
      {
        type: 'text',
        text: 'You agree to provide accurate, current, and complete information when creating your account and to keep this information up to date. We reserve the right to suspend or terminate accounts that contain false or misleading information.',
      },
      {
        type: 'subheading',
        text: '3.4 One Account Per Person',
      },
      {
        type: 'text',
        text: 'Each individual may hold one free account. Creating multiple accounts to circumvent usage limits (including the AI coaching report limit) is prohibited and may result in suspension of all associated accounts.',
      },
    ],
  },
  {
    id: 'acceptable-use',
    title: '4. Acceptable Use',
    content: [
      {
        type: 'text',
        text: 'You agree to use KFX Journal only for lawful purposes and in a manner consistent with these Terms. You must not:',
      },
      {
        type: 'list',
        items: [
          'Use the platform to engage in any fraudulent, deceptive, or misleading activity',
          'Attempt to gain unauthorised access to any part of the platform or another user\'s account',
          'Reverse-engineer, decompile, or otherwise attempt to extract the source code of the platform',
          'Use automated tools (bots, scrapers, crawlers) to access the platform without our written consent',
          'Upload trade data that you do not own or are not authorised to use',
          'Resell, sublicense, or commercialise access to the platform or its features without our written permission',
          'Use the AI coaching feature to generate reports for traders other than yourself without our written permission',
          'Intentionally overload or disrupt the platform\'s infrastructure',
          'Upload malicious files, scripts, or code',
          'Impersonate any person, organisation, or entity',
        ],
      },
      {
        type: 'text',
        text: 'Violation of these rules may result in immediate account suspension or termination without notice.',
      },
    ],
  },
  {
    id: 'ai-feature',
    title: '5. AI Coaching Feature — Terms of Use',
    content: [
      {
        type: 'subheading',
        text: '5.1 What the AI coach does',
      },
      {
        type: 'text',
        text: 'The AI coaching feature sends a structured summary of your trade history to Anthropic\'s Claude API and returns a personalised coaching report. The report may include observations about your trading patterns, rule adherence, and areas for improvement.',
      },
      {
        type: 'subheading',
        text: '5.2 Not financial advice',
      },
      {
        type: 'highlight',
        text: 'The AI coaching report is for educational and analytical purposes only. It does not constitute financial advice, investment recommendations, or trading signals. Do not make trading decisions based solely on AI coaching output. You are solely responsible for your trading decisions and their outcomes.',
      },
      {
        type: 'subheading',
        text: '5.3 Usage limits',
      },
      {
        type: 'text',
        text: 'During early access, free accounts are entitled to 1 AI coaching report per calendar month. This limit exists because AI generation has a real cost. We reserve the right to adjust usage limits with 14 days\' notice. Users on future paid plans will receive higher or unlimited generation limits as specified in their plan.',
      },
      {
        type: 'subheading',
        text: '5.4 AI output accuracy',
      },
      {
        type: 'text',
        text: 'AI-generated coaching reports are based on the trade data you have logged and may contain errors, misinterpretations, or inaccurate observations. The quality of the report depends entirely on the accuracy and completeness of the trade data you provide. We do not guarantee the accuracy, completeness, or fitness of any AI-generated output.',
      },
    ],
  },
  {
    id: 'data-ownership',
    title: '6. Your Data — Ownership & Licence',
    content: [
      {
        type: 'subheading',
        text: '6.1 You own your data',
      },
      {
        type: 'text',
        text: 'All trade records, journal entries, notes, screenshots, and other content you upload to KFX Journal belong to you. We do not claim ownership of your trading data.',
      },
      {
        type: 'subheading',
        text: '6.2 Licence to us',
      },
      {
        type: 'text',
        text: 'By uploading content to KFX Journal, you grant us a limited, non-exclusive, royalty-free licence to store, process, and display your content solely for the purpose of providing the platform to you. This licence ends when you delete the content or close your account.',
      },
      {
        type: 'subheading',
        text: '6.3 Anonymised analytics',
      },
      {
        type: 'text',
        text: 'We may use aggregated, anonymised, and non-identifiable data (for example, "what percentage of traders improve win rate after 30 days of journaling") to improve the platform and publish general trading insights. This data cannot be traced back to any individual user.',
      },
      {
        type: 'subheading',
        text: '6.4 Data export',
      },
      {
        type: 'text',
        text: 'You may export your complete trade history as a CSV file at any time from Settings → Export. We will never hold your data hostage or make export contingent on a paid plan.',
      },
    ],
  },
  {
    id: 'pricing',
    title: '7. Pricing, Plans & Payments',
    content: [
      {
        type: 'subheading',
        text: '7.1 Early access — free plan',
      },
      {
        type: 'text',
        text: 'KFX Journal is currently in early access. All features are available on the free plan, with the exception that AI coaching reports are limited to 1 per calendar month. No payment information is required to use the free plan.',
      },
      {
        type: 'subheading',
        text: '7.2 Future paid plans',
      },
      {
        type: 'text',
        text: 'Pro and Lifetime plans are displayed on the pricing page but are not currently available for purchase. When paid plans become available, we will notify all users by email with full pricing details and at least 14 days\' notice before any free features become restricted.',
      },
      {
        type: 'subheading',
        text: '7.3 Pricing changes',
      },
      {
        type: 'text',
        text: 'We reserve the right to change pricing for paid plans at any time. Active paid subscribers will be notified at least 30 days before any price increase takes effect and may cancel before the new price applies.',
      },
      {
        type: 'subheading',
        text: '7.4 Refunds',
      },
      {
        type: 'text',
        text: 'For monthly subscriptions, you may cancel at any time and retain access until the end of your billing period. No partial refunds are issued for unused days. For Lifetime plans, we offer a 14-day full refund if you are not satisfied — contact info@kfxjournal.com within 14 days of purchase.',
      },
    ],
  },
  {
    id: 'intellectual-property',
    title: '8. Intellectual Property',
    content: [
      {
        type: 'text',
        text: 'All rights in the KFX Journal platform — including its design, code, branding, analytics methodology, AI prompting systems, and user interface — are owned by KFX Journal. These Terms do not grant you any intellectual property rights in the platform.',
      },
      {
        type: 'text',
        text: '"KFX Journal" and "KFX" are trading names. You may not use our name, logo, or branding without prior written permission.',
      },
      {
        type: 'text',
        text: 'If you provide feedback, suggestions, or ideas about the platform, you agree that we may use this feedback without obligation to you.',
      },
    ],
  },
  {
    id: 'disclaimers',
    title: '9. Disclaimers',
    content: [
      {
        type: 'highlight',
        text: 'KFX Journal is provided "as is" and "as available" without warranties of any kind, express or implied. We do not warrant that the platform will be uninterrupted, error-free, or free from security vulnerabilities.',
      },
      {
        type: 'list',
        items: [
          'We do not warrant the accuracy of any analytics, statistics, or AI-generated coaching reports',
          'We do not guarantee that using KFX Journal will improve your trading performance',
          'We are not responsible for any trading losses you incur, whether or not you used KFX Journal insights in your decision-making',
          'We do not guarantee continuous availability — the platform may be unavailable due to maintenance, upgrades, or circumstances outside our control',
        ],
      },
      {
        type: 'text',
        text: 'To the fullest extent permitted by applicable law, KFX Journal disclaims all implied warranties, including merchantability, fitness for a particular purpose, and non-infringement.',
      },
    ],
  },
  {
    id: 'limitation',
    title: '10. Limitation of Liability',
    content: [
      {
        type: 'text',
        text: 'To the maximum extent permitted by applicable law, KFX Journal and its founders, employees, and contractors shall not be liable for:',
      },
      {
        type: 'list',
        items: [
          'Any trading losses or financial damages, whether or not related to use of the platform',
          'Loss of data due to technical failure, user error, or circumstances outside our control',
          'Indirect, incidental, special, consequential, or punitive damages',
          'Any damages arising from reliance on AI-generated coaching reports',
          'Service interruptions or downtime',
        ],
      },
      {
        type: 'text',
        text: 'In jurisdictions that do not allow exclusion or limitation of certain damages, our liability is limited to the maximum extent permitted by law. In no event shall our aggregate liability to you exceed the amount you paid to us in the 12 months preceding the claim.',
      },
    ],
  },
  {
    id: 'termination',
    title: '11. Account Termination',
    content: [
      {
        type: 'subheading',
        text: '11.1 By you',
      },
      {
        type: 'text',
        text: 'You may delete your account at any time from Settings → Account → Delete Account. Deletion permanently removes all your data within 24 hours. This action is irreversible.',
      },
      {
        type: 'subheading',
        text: '11.2 By us',
      },
      {
        type: 'text',
        text: 'We reserve the right to suspend or terminate your account immediately and without notice if you violate these Terms, engage in fraudulent activity, or use the platform in a manner that harms other users or the platform itself.',
      },
      {
        type: 'subheading',
        text: '11.3 Effect of termination',
      },
      {
        type: 'text',
        text: 'Upon termination, your right to access the platform ends immediately. You should export your data before closing your account. We are not obligated to retain your data after termination.',
      },
    ],
  },
  {
    id: 'governing-law',
    title: '12. Governing Law',
    content: [
      {
        type: 'text',
        text: 'These Terms are governed by and construed in accordance with the laws of Rwanda. Any disputes arising from or relating to these Terms or your use of KFX Journal shall be subject to the exclusive jurisdiction of the courts of Rwanda.',
      },
      {
        type: 'text',
        text: 'If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.',
      },
    ],
  },
  {
    id: 'changes',
    title: '13. Changes to These Terms',
    content: [
      {
        type: 'text',
        text: 'We may revise these Terms at any time. When we make material changes, we will notify you by email and display a notice in the app at least 14 days before the new Terms take effect. The "Last Updated" date at the top reflects the current version.',
      },
      {
        type: 'text',
        text: 'Continued use of KFX Journal after the effective date of revised Terms constitutes your acceptance of those Terms.',
      },
    ],
  },
  {
    id: 'contact',
    title: '14. Contact',
    content: [
      {
        type: 'text',
        text: 'For any questions about these Terms, please contact us:',
      },
      {
        type: 'contact',
        items: [
          { label: 'Email', value: CONTACT_EMAIL },
          { label: 'Product', value: 'KFX Journal — Trading Intelligence Platform' },
          { label: 'Response time', value: 'Within 2 business days' },
        ],
      },
    ],
  },
];

function PolicySection({ section }) {
  return (
    <div id={section.id} style={{ marginBottom: 48, scrollMarginTop: 80 }}>
      <h2 style={{
        fontSize: 18, fontWeight: 700, color: 'var(--text)',
        letterSpacing: '-0.01em', marginBottom: 18,
        paddingBottom: 12, borderBottom: '1px solid rgba(212,168,67,0.12)',
        fontFamily: "'Bricolage Grotesque',sans-serif",
      }}>
        {section.title}
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {section.content.map((block, i) => {
          if (block.type === 'text') return (
            <p key={i} style={{ fontSize: 14.5, color: 'var(--text-60)', lineHeight: 1.85, fontFamily: "'Bricolage Grotesque',sans-serif" }}>
              {block.text}
            </p>
          );
          if (block.type === 'subheading') return (
            <h3 key={i} style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', letterSpacing: '0.01em', marginTop: 8, fontFamily: "'Bricolage Grotesque',sans-serif" }}>
              {block.text}
            </h3>
          );
          if (block.type === 'highlight') return (
            <div key={i} style={{
              padding: '14px 16px', borderRadius: 10,
              background: 'rgba(212,168,67,0.06)',
              border: '1px solid rgba(212,168,67,0.2)',
              borderLeft: '3px solid rgba(212,168,67,0.6)',
            }}>
              <p style={{ fontSize: 14, color: 'var(--text-70)', lineHeight: 1.8, fontFamily: "'Bricolage Grotesque',sans-serif", margin: 0 }}>
                {block.text}
              </p>
            </div>
          );
          if (block.type === 'list') return (
            <ul key={i} style={{ paddingLeft: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8, listStyle: 'none' }}>
              {block.items.map((item, j) => (
                <li key={j} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(212,168,67,0.6)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 4 }}>
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span style={{ fontSize: 14, color: 'var(--text-55)', lineHeight: 1.75, fontFamily: "'Bricolage Grotesque',sans-serif" }}>{item}</span>
                </li>
              ))}
            </ul>
          );
          if (block.type === 'contact') return (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '16px 20px', borderRadius: 10, background: 'var(--overlay-2)', border: '1px solid var(--overlay-7)' }}>
              {block.items.map((item, j) => (
                <div key={j} style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: 'rgba(212,168,67,0.6)', letterSpacing: '0.1em', textTransform: 'uppercase', minWidth: 100, flexShrink: 0 }}>{item.label}</span>
                  <span style={{ fontSize: 14, color: 'var(--text-65)', fontFamily: "'Bricolage Grotesque',sans-serif" }}>
                    {item.label === 'Email' ? <a href={`mailto:${item.value}`} style={{ color: 'var(--gold)', textDecoration: 'none', wordBreak: 'break-all' }}>{item.value}</a> : item.value}
                  </span>
                </div>
              ))}
            </div>
          );
          return null;
        })}
      </div>
    </div>
  );
}

export default function TermsOfService({ onNavigate }) {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg, #070707)',
      color: 'var(--text, #EDE8DE)',
      fontFamily: "'Bricolage Grotesque',sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,600;12..96,700;12..96,800&family=DM+Mono:wght@400;500&display=swap');
        :root {
          --bg: #070707; --surface: #0f0f0d; --text: #EDE8DE;
          --text-70: rgba(237,232,222,0.70); --text-65: rgba(237,232,222,0.65);
          --text-60: rgba(237,232,222,0.60); --text-55: rgba(237,232,222,0.55);
          --text-45: rgba(237,232,222,0.45);
          --gold: #D4A843; --overlay-2: rgba(255,255,255,0.02);
          --overlay-5: rgba(255,255,255,0.05); --overlay-7: rgba(255,255,255,0.07);
        }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }

        /* ── Mobile ── */
        @media (max-width: 700px) {
          /* Layout: single column */
          .kfx-layout {
            grid-template-columns: 1fr !important;
            gap: 0 !important;
            padding: 32px 16px 72px !important;
          }

          /* Sidebar: collapse to compact card above content */
          .kfx-sidebar {
            position: static !important;
            margin-bottom: 28px;
            padding: 12px 14px;
            background: rgba(255,255,255,0.02);
            border: 1px solid rgba(212,168,67,0.1);
            border-radius: 10px;
          }
          .kfx-sidebar-label { margin-bottom: 10px !important; }
          .kfx-toc-nav {
            flex-direction: row !important;
            flex-wrap: wrap !important;
            gap: 4px !important;
          }
          .kfx-toc-nav a {
            font-size: 11px !important;
            padding: 4px 8px !important;
            background: rgba(212,168,67,0.04) !important;
            border-radius: 5px;
            line-height: 1.3 !important;
          }
          .kfx-sidebar-also { margin-top: 12px !important; }

          /* Nav: tighten padding, hide subtitle */
          .kfx-nav { padding: 0 16px !important; }
          .kfx-nav-subtitle { display: none !important; }
          .kfx-back-btn {
            font-size: 10px !important;
            padding: 6px 12px !important;
            letter-spacing: 0.04em !important;
          }

          /* Header section */
          .kfx-page-header { margin-bottom: 32px !important; }
          .kfx-page-header h1 { font-size: 28px !important; }


        }
      `}</style>

      {/* Nav */}
      <nav className="kfx-nav" style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(7,7,7,0.92)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(212,168,67,0.1)', padding: '0 24px', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button onClick={() => onNavigate('home')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.01em' }}>KFX Journal</div>
          <div className="kfx-nav-subtitle" style={{ fontSize: 8, color: 'rgba(212,168,67,0.5)', fontFamily: "'DM Mono',monospace", letterSpacing: '0.1em', textTransform: 'uppercase' }}>Trading Intelligence</div>
        </button>
        <button className="kfx-back-btn" onClick={() => onNavigate('home')} style={{ background: 'none', border: '1px solid rgba(212,168,67,0.2)', borderRadius: 7, padding: '7px 16px', color: 'var(--gold)', fontSize: 11, fontFamily: "'DM Mono',monospace", letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(212,168,67,0.08)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
        >← Back</button>
      </nav>

      <div className="kfx-layout" style={{ maxWidth: 860, margin: '0 auto', padding: '72px 24px 120px', display: 'grid', gridTemplateColumns: '220px 1fr', gap: 60, alignItems: 'start' }}>

        {/* Sidebar TOC */}
        <aside className="kfx-sidebar" style={{ position: 'sticky', top: 80 }}>
          <div className="kfx-sidebar-label" style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: 'rgba(212,168,67,0.6)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 16 }}>On this page</div>
          <nav className="kfx-toc-nav" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {sections.map(s => (
              <a key={s.id} href={`#${s.id}`} style={{ fontSize: 13, color: 'var(--text-45)', textDecoration: 'none', padding: '5px 10px', borderRadius: 6, transition: 'all 0.2s', fontFamily: "'Bricolage Grotesque',sans-serif", lineHeight: 1.4 }}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--text)'; e.currentTarget.style.background = 'rgba(212,168,67,0.06)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-45)'; e.currentTarget.style.background = 'none'; }}
              >{s.title}</a>
            ))}
          </nav>
          <div className="kfx-sidebar-also" style={{ marginTop: 24, padding: '14px 16px', borderRadius: 8, background: 'rgba(212,168,67,0.04)', border: '1px solid rgba(212,168,67,0.12)' }}>
            <div style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: 'rgba(212,168,67,0.5)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>Also read</div>
            <button onClick={() => onNavigate('privacy')} style={{ fontSize: 12, color: 'var(--gold)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Bricolage Grotesque',sans-serif", padding: 0 }}>Privacy Policy →</button>
          </div>
        </aside>

        {/* Main content */}
        <main>
          {/* Header */}
          <div className="kfx-page-header" style={{ marginBottom: 56 }}>
            <div style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: 'rgba(212,168,67,0.6)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 12 }}>Legal</div>
            <h1 style={{ fontSize: 'clamp(28px,5vw,48px)', fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.025em', lineHeight: 1.1, marginBottom: 16 }}>Terms of Service</h1>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: 'var(--text-45)', fontFamily: "'DM Mono',monospace" }}>Last updated: {LAST_UPDATED}</span>
              <span style={{ fontSize: 12, color: 'var(--text-45)', fontFamily: "'DM Mono',monospace" }}>Effective: {LAST_UPDATED}</span>
            </div>
            <div style={{ marginTop: 20, padding: '14px 16px', borderRadius: 8, background: 'rgba(212,168,67,0.05)', border: '1px solid rgba(212,168,67,0.18)' }}>
              <p style={{ fontSize: 13.5, color: 'rgba(212,168,67,0.8)', lineHeight: 1.7, fontFamily: "'Bricolage Grotesque',sans-serif" }}>
                <strong>Short version:</strong> Use KFX Journal honestly, don't abuse it, own your data, and understand that trading insights from the platform are not financial advice. We're a small early-access product — we'll be transparent with you about changes.
              </p>
            </div>
          </div>

          {/* Sections */}
          {sections.map(section => (
            <PolicySection key={section.id} section={section} />
          ))}
        </main>
      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid rgba(212,168,67,0.1)', padding: '28px 24px', textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: '6px 10px', fontSize: 12, color: 'var(--text-45)', fontFamily: "'DM Mono',monospace", letterSpacing: '0.04em' }}>
          <span>© {new Date().getFullYear()} KFX Journal</span>
          <span style={{ opacity: 0.35 }}>·</span>
          <button onClick={() => onNavigate('terms')} style={{ color: 'var(--gold)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Mono',monospace", fontSize: 12, padding: 0, letterSpacing: '0.04em' }}>Terms of Service</button>
          <span style={{ opacity: 0.35 }}>·</span>
          <button onClick={() => onNavigate('privacy')} style={{ color: 'var(--text-45)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Mono',monospace", fontSize: 12, padding: 0, letterSpacing: '0.04em' }}>Privacy Policy</button>
          <span style={{ opacity: 0.35 }}>·</span>
          <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: 'var(--text-45)', textDecoration: 'none' }}>{CONTACT_EMAIL}</a>
        </div>
      </div>
    </div>
  );
}